import { capitalize, isNull, isUndefined, toNumber } from 'lodash'
import moment from 'moment'
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber'

const isEmpty = (value: any) => isUndefined(value) || isNull(value) || value.length === 0

export const email = (value: string) => {
  if (isEmpty(value)) {
    return 'Required'
  } else if (!/^[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)) {
    return 'Invalid email address'
  }
}

export const required = (value: any) => {
  if (isEmpty(value)) {
    return 'Required'
  }
}

const phoneUtil = PhoneNumberUtil.getInstance()
// Optional field: empty passes. Numbers must be in international format (+<country code><number>).
// `example` is the country-specific hint shown in the error, e.g. '+61 0000 0000'.
export const phone = (example: string) => (value: string) => {
  if (isEmpty(value) || !value.trim()) {
    return
  }

  if (!value.trim().startsWith('+')) {
    return `Use international format, e.g. ${example}`
  }

  try {
    if (!phoneUtil.isValidNumber(phoneUtil.parse(value))) {
      return 'Invalid phone number'
    }
  } catch {
    return 'Invalid phone number'
  }
}

// Converts to E.164 ("+16502530000") for the API. Empty/unparsable values are returned unchanged.
export const toInternationalPhone = (value?: string | null) => {
  if (!value || !value.trim()) {
    return value
  }

  try {
    return phoneUtil.format(phoneUtil.parse(value), PhoneNumberFormat.E164)
  } catch {
    return value
  }
}

// redux-form `normalize`: keeps the input in E.164 form ("+" followed by digits only)
export const normalizePhone = (value: string) => {
  // Blur is dispatched without a value; keep the current one instead of clearing it
  if (value === undefined) {
    return value
  }

  const digits = (value || '').replace(/\D/g, '')

  // Lets the user clear the field
  return digits ? `+${digits}` : ''
}

// Select option field: an empty option object ({}) also counts as not selected.
export const requiredOption = (value?: { value?: unknown } | null) => {
  if (isEmpty(value?.value)) {
    return 'Required'
  }
}

export const requiredEmpty = (value: any) => {
  if (isEmpty(value)) {
    return ' '
  }
}

export const requiredBoolean = (value: boolean) => {
  if (!value) {
    return 'Required'
  }
}

export const validDateFormat = (format: string) => {
  const formattedResult = moment().format(format)

  if (!moment(formattedResult, format).isValid() || moment().format(format) === format) {
    return 'Format is not valid'
  }
}

export const password = (value: string) => {
  const regexp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!#$%&?_"])[a-zA-Z\d!#$%&?_"]{8,}$/

  if (!regexp.test(value)) {
    return `Minimum 8 characters, including a number, lowercase letter, uppercase letter, and a symbol from (!#$%&?_")`
  }
}

export const passwordsMatch = (value: string, allValues: Record<'password', string>) => {
  if (value !== allValues.password) {
    return "Password doesn't match"
  }
}

export const positive = (value: number | string) => {
  if (value && toNumber(value) < 0) {
    return 'Must be positive'
  }
}

export const greaterThanZero = (value: string) => {
  if (value && toNumber(value) <= 0) {
    return 'Must be greater than 0'
  }
}

export const uniq = (list: string[], key: string) => (value: string) => {
  if (list.includes(value)) {
    return `${capitalize(key)} already exists`
  }
}

export const minValue = (min: number) => (value: string) => {
  if (!min) {
    return
  }

  if (toNumber(value) < min) {
    return `Must be greater than ${min}`
  }
}

export const maxValue = (max: number) => (value: string) => {
  if (!max) {
    return
  }

  if (toNumber(value) > max) {
    return `Must be less than ${max}`
  }
}

export const maxLength = (max: number) => (value: string) => {
  if (!max) {
    return
  }

  if (value.length > max) {
    return `Must be less than ${max} characters long`
  }
}

export type ValidatorFn = (...args: any[]) => string | void
export type Validators = ValidatorFn | ValidatorFn[]

export const minLength = (min: number) => (value: string) => {
  if (!min) {
    return
  }

  if (value.length < min) {
    return `Please enter at least ${min} characters`
  }
}

export const ALLOWED_MIME_TYPES = [
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/tiff',

  // Documents
  'text/plain', // .txt
  'application/rtf', // .rtf
  'application/pdf',
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx

  // Presentations
  'application/vnd.ms-powerpoint', // .ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx

  // Spreadsheets
  'text/csv', // .csv
  'application/vnd.ms-excel', // .xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx

  // Email files
  '.msg', // .msg for macOS
  'message/rfc822', // .eml
  'application/vnd.ms-outlook', // .msg
  'application/vnd.ms-outlook.message', // .msg

  // Video
  'video/mp4',
  'video/quicktime', // .mov

  // Audio
  'audio/mpeg', // .mp3

  // Archives
  'application/zip'
]

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/tiff']

export const minValue300 = (value: string) => {
  if (value && toNumber(value) < 300) {
    return 'Must be greater than 300'
  }
}
